"use client";

import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { ChevronDown, Calendar } from "lucide-react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import VideoUploadInput from "@/components/form/VideoUploadInput";
import { supportTicketFormFieldTextClassName } from "@/app/profile/inbox/(chats)/components/SupportTickets/supportTicketMessageUtils";
import { useGetOrdersQuery } from "@/features/order/orderApiInjection";
import { useCreateCompanyReviewMutation } from "@/features/company-reviews/companyReviewInjection";
import { ga4CompanyReviewSubmit } from "@/lib/analytics/ga4-ecommerce";
import { OrderStatus } from "@/types/orders";
import LoadingSpinner from "@/components/loadingComponents/loadingSpinner";
import { cn } from "@/lib/utils";
import type { Order } from "@/types/orders";

const CUSTOM = "__custom__";
type ReviewType = "text" | "video";
type ReviewOrderOption = {
	value: string;
	orderId: string;
	label: string;
};

const glassFormFieldClassName = cn(
	"h-10 min-w-0 border-secondary/35 bg-white/85 shadow-sm backdrop-blur-2xl placeholder:text-muted-foreground focus-visible:border-secondary focus-visible:ring-secondary/30 dark:bg-background/25",
	supportTicketFormFieldTextClassName,
);

const glassFormDropdownTriggerClassName = cn(
	"relative flex h-10 w-full min-w-0 items-center rounded-md border border-secondary/35 bg-white/85 px-3 pr-10 text-left shadow-sm outline-none backdrop-blur-2xl transition-colors hover:border-secondary hover:bg-secondary/10 dark:bg-background/25",
	supportTicketFormFieldTextClassName,
);

const glassFormDropdownMenuClassName =
	"absolute z-50 mt-1 max-h-52 w-full overflow-auto rounded-xl border border-secondary/35 bg-white/95 p-1 shadow-2xl shadow-secondary/25 backdrop-blur-[32px] dark:bg-background/80";

const glassFormReviewBodyHeightClassName = "h-[8.75rem]";

const glassFormDateFieldClassName = cn(
	glassFormFieldClassName,
	"pr-10 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:top-0 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-10 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:!opacity-0 [&::-webkit-calendar-picker-indicator]:![filter:none]",
);

const glassFormFieldLabelClassName = "grid min-w-0 gap-1.5 text-sm";

function localYyyyMmDd(d = new Date()): string {
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
}

type FormValues = {
	title: string;
	body: string;
	videoUrl: string;
	videoThumbnailUrl: string;
};

export type WriteCompanyReviewDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	userId: string;
	/** Pre-select this order (e.g. from order history “Submit a Review”). */
	defaultOrderId?: string | null;
};

function orderDateLabel(createdAt?: string): string {
	if (!createdAt) return "";
	const date = new Date(createdAt);
	return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString();
}

function orderOptionLabel(title: string, orderRef: string, date: string): string {
	if (title === orderRef) {
		return [orderRef, date].filter(Boolean).join(" · ");
	}
	return [title, orderRef, date].filter(Boolean).join(" · ");
}

function buildReviewOrderOptions(orders: Order[]): ReviewOrderOption[] {
	return orders.flatMap((order) => {
		const orderRef = `Order ${order.id.slice(-6)}`;
		const date = orderDateLabel(order.createdAt);
		if (order.items.length === 0) {
			return [
				{
					value: order.id,
					orderId: order.id,
					label: orderOptionLabel(orderRef, orderRef, date),
				},
			];
		}

		return order.items.map((item, index) => {
			const title = item.serviceId?.title?.trim() || orderRef;
			return {
				value: `${order.id}:${item._id || index}`,
				orderId: order.id,
				label: orderOptionLabel(title, orderRef, date),
			};
		});
	});
}

function getDefaultOrderSelection(options: ReviewOrderOption[]): string {
	if (options.length === 0) return CUSTOM;
	return options[0]!.value;
}

export default function WriteCompanyReviewDialog({
	open,
	onOpenChange,
	userId,
	defaultOrderId = null,
}: WriteCompanyReviewDialogProps) {
	const { toast } = useToast();
	const [createReview, { isLoading: isSubmitting }] = useCreateCompanyReviewMutation();

	const { data: ordersData, isLoading: ordersLoading } = useGetOrdersQuery(
		{
			userId,
			pagination: false,
			orderStatus: OrderStatus.PAID,
		},
		{ skip: !open || !userId },
	);

	const orders = useMemo(() => ordersData?.docs ?? [], [ordersData?.docs]);
	const orderOptions = useMemo(() => buildReviewOrderOptions(orders), [orders]);
	const allOrderOptions = useMemo<ReviewOrderOption[]>(() => {
		if (orderOptions.length === 0) {
			return [
				{
					value: CUSTOM,
					orderId: CUSTOM,
					label: "Custom project (no paid orders yet)",
				},
			];
		}
		return [
			...orderOptions,
			{
				value: CUSTOM,
				orderId: CUSTOM,
				label: "Custom project (not tied to an order)",
			},
		];
	}, [orderOptions]);

	const form = useForm<FormValues>({
		defaultValues: {
			title: "",
			body: "",
			videoUrl: "",
			videoThumbnailUrl: "",
		},
	});

	const [rating, setRating] = useState(5);
	const [orderSelection, setOrderSelection] = useState<string | null>(null);
	const [reviewDate, setReviewDate] = useState(localYyyyMmDd);
	const [reviewType, setReviewType] = useState<ReviewType>("video");
	const [orderDropdownOpen, setOrderDropdownOpen] = useState(false);

	useEffect(() => {
		if (!open) {
			form.reset({ title: "", body: "", videoUrl: "", videoThumbnailUrl: "" });
			setRating(5);
			setOrderSelection(null);
			setReviewType("video");
			setOrderDropdownOpen(false);
		} else {
			setReviewDate(localYyyyMmDd());
		}
	}, [open, form]);

	useEffect(() => {
		if (!open || ordersLoading || orderSelection !== null) return;
		const preferred =
			defaultOrderId &&
			orderOptions.find((option) => option.orderId === defaultOrderId)?.value;
		setOrderSelection(
			preferred ? preferred : getDefaultOrderSelection(orderOptions),
		);
	}, [open, ordersLoading, orderOptions, orderSelection, defaultOrderId]);

	const formReady = !ordersLoading && orderSelection !== null;

	const selectedOrderLabel =
		allOrderOptions.find((option) => option.value === orderSelection)?.label ??
		"Select related order";

	async function onSubmit(values: FormValues) {
		if (orderSelection === null) return;
		if (reviewType === "text" && !values.body?.trim()) {
			toast({
				variant: "destructive",
				title: "Review text required",
				description: "Add review text, or choose Video review instead.",
			});
			return;
		}
		if (reviewType === "video" && !values.videoUrl?.trim()) {
			toast({
				variant: "destructive",
				title: "Video required",
				description: "Upload a video, or choose Text review instead.",
			});
			return;
		}

		const isCustom = orderSelection === CUSTOM || orderOptions.length === 0;
		const selectedOrderId =
			orderOptions.find((option) => option.value === orderSelection)?.orderId ??
			orderSelection;
		const payload = {
			rating,
			title: values.title.trim(),
			body: values.body?.trim() || undefined,
			videoUrl: values.videoUrl?.trim() || undefined,
			videoThumbnailUrl: values.videoThumbnailUrl?.trim() || undefined,
			isCustomProject: isCustom,
			reviewDate: reviewDate.trim() || localYyyyMmDd(),
			...(isCustom ? {} : { orderId: selectedOrderId }),
		};

		try {
			await createReview(payload).unwrap();
			ga4CompanyReviewSubmit({ rating, review_type: reviewType });
			toast({
				title: "Review submitted",
				description: "Thank you. Your review will appear after the team approves it.",
			});
			onOpenChange(false);
		} catch (e: unknown) {
			const message =
				e && typeof e === "object" && "data" in e
					? String((e as { data?: { message?: string } }).data?.message ?? "Something went wrong.")
					: "Something went wrong.";
			toast({
				variant: "destructive",
				title: "Could not submit review",
				description: message,
			});
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				hideClose
				className="flex max-h-[90vh] flex-col gap-4 overflow-hidden border border-secondary/25 bg-white/90 text-foreground shadow-2xl shadow-secondary/20 backdrop-blur-3xl dark:bg-background/45 sm:max-w-lg"
				onInteractOutside={(event) => event.preventDefault()}
				onPointerDownOutside={(event) => event.preventDefault()}
				onEscapeKeyDown={(event) => event.preventDefault()}
			>
				<DialogHeader className="shrink-0">
					<DialogTitle className="text-foreground">Write a review</DialogTitle>
					<DialogDescription className="text-muted-foreground">
						Share your experience with Techxeed.
					</DialogDescription>
				</DialogHeader>

				<div className="min-h-0 min-w-0 flex-1 overflow-y-auto">
				{!formReady ? (
					<div className="flex justify-center py-8">
						<LoadingSpinner />
					</div>
				) : (
					<form
						onSubmit={form.handleSubmit(onSubmit)}
						className="min-w-0 space-y-4"
						noValidate
					>
						<label className={glassFormFieldLabelClassName}>
							<span className="text-xs font-medium text-muted-foreground">Rating</span>
							<div
								className="flex items-center gap-1"
								role="group"
								aria-label="Star rating"
							>
								{[1, 2, 3, 4, 5].map((star) => (
									<button
										key={star}
										type="button"
										onClick={() => setRating(star)}
										className={cn(
											"inline-flex size-5 items-center justify-center rounded-sm text-xs leading-none transition-colors",
											"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/30 focus-visible:ring-offset-2",
											star <= rating
												? "bg-tertiary text-white"
												: "bg-secondary/15 text-muted-foreground",
										)}
										aria-label={`${star} stars`}
									>
										★
									</button>
								))}
							</div>
						</label>

						<label className={glassFormFieldLabelClassName}>
							<span className="text-xs font-medium text-muted-foreground">
								Related order
							</span>
							<div className="relative min-w-0">
								<button
									type="button"
									className={glassFormDropdownTriggerClassName}
									disabled={orderOptions.length === 0}
									onClick={() => setOrderDropdownOpen((current) => !current)}
								>
									<span className="min-w-0 truncate">{selectedOrderLabel}</span>
									<span className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
										<ChevronDown
											className={cn(
												"size-4 stroke-secondary transition-transform",
												orderDropdownOpen && "rotate-180",
											)}
											aria-hidden
										/>
									</span>
								</button>
								{orderDropdownOpen ? (
									<div className={glassFormDropdownMenuClassName}>
										{allOrderOptions.map((option) => (
											<button
												key={option.value}
												type="button"
												className={cn(
													"block w-full rounded-lg px-3 py-2 text-left text-xs text-foreground transition-colors hover:bg-secondary/15",
													orderSelection === option.value &&
														"bg-secondary text-secondary-foreground hover:bg-secondary",
												)}
												onClick={() => {
													setOrderSelection(option.value);
													setOrderDropdownOpen(false);
												}}
											>
												{option.label}
											</button>
										))}
									</div>
								) : null}
							</div>
						</label>

						<label className={glassFormFieldLabelClassName}>
							<span className="text-xs font-medium text-muted-foreground">
								Review date
							</span>
							<div className="relative min-w-0">
								<Input
									id="company-review-date"
									type="date"
									className={glassFormDateFieldClassName}
									value={reviewDate}
									max={localYyyyMmDd()}
									onChange={(event) => setReviewDate(event.target.value)}
								/>
								<Calendar
									className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 stroke-secondary"
									aria-hidden
								/>
							</div>
							<p className="text-xs text-muted-foreground">
								Defaults to today. You can choose an earlier date if this reflects when the
								work happened.
							</p>
						</label>

						<label className={glassFormFieldLabelClassName}>
							<span className="text-xs font-medium text-muted-foreground">Title</span>
							<Input
								id="company-review-title"
								className={glassFormFieldClassName}
								placeholder="Summarize your experience"
								{...form.register("title", { required: "Title is required" })}
							/>
							{form.formState.errors.title ? (
								<p className="text-xs text-destructive">
									{form.formState.errors.title.message}
								</p>
							) : null}
						</label>

						<label className={glassFormFieldLabelClassName}>
							<span className="text-xs font-medium text-muted-foreground">Review type</span>
							<div className="h-9 rounded-full border border-secondary/35 bg-muted/20 p-1 shadow-xs">
								<div className="grid h-full grid-cols-2 gap-0.5">
									<button
										type="button"
										onClick={() => setReviewType("video")}
										className={cn(
											"flex items-center justify-center rounded-full px-3 text-xs font-medium transition",
											reviewType === "video"
												? "bg-secondary text-white shadow-sm"
												: "text-muted-foreground hover:bg-secondary/10",
										)}
									>
										Video review
									</button>
									<button
										type="button"
										onClick={() => setReviewType("text")}
										className={cn(
											"flex items-center justify-center rounded-full px-3 text-xs font-medium transition",
											reviewType === "text"
												? "bg-secondary text-white shadow-sm"
												: "text-muted-foreground hover:bg-secondary/10",
										)}
									>
										Text review
									</button>
								</div>
							</div>
						</label>

						<FormProvider {...form}>
							<div className={glassFormFieldLabelClassName}>
								<span className="text-xs font-medium text-muted-foreground">
									{reviewType === "text" ? "Review" : "Video review"}
								</span>
								<div
									className={cn(
										"relative min-w-0",
										glassFormReviewBodyHeightClassName,
									)}
								>
									<div
										className={cn(
											"absolute inset-0 flex min-w-0 flex-col overflow-hidden rounded-md border border-secondary/35 bg-white/85 shadow-sm backdrop-blur-2xl dark:bg-background/25",
											reviewType !== "text" &&
												"pointer-events-none invisible",
										)}
										aria-hidden={reviewType !== "text"}
									>
										<Textarea
											id="company-review-body"
											tabIndex={reviewType === "text" ? 0 : -1}
											className={cn(
												"h-full min-h-0 flex-1 resize-none rounded-none border-0 bg-transparent px-3 py-3 shadow-none placeholder:text-muted-foreground focus-visible:ring-0",
												supportTicketFormFieldTextClassName,
											)}
											{...form.register("body", {
												validate: (v) =>
													reviewType !== "text" ||
													(v?.trim()?.length ?? 0) > 0 ||
													"Enter your review",
											})}
											placeholder="Share details about delivery, quality, and communication"
										/>
									</div>
									<div
										className={cn(
											"absolute inset-0 min-w-0",
											reviewType !== "video" &&
												"pointer-events-none invisible",
										)}
										aria-hidden={reviewType !== "video"}
									>
										<VideoUploadInput
											control={form.control}
											name="videoUrl"
											entityId={`company-review-${userId}`}
											variant="glass"
											fillContainer
											onThumbnailUploaded={(thumbnailUrl) =>
												form.setValue("videoThumbnailUrl", thumbnailUrl, {
													shouldDirty: true,
													shouldValidate: false,
												})
											}
										/>
									</div>
								</div>
								<input type="hidden" {...form.register("videoThumbnailUrl")} />
								{reviewType === "text" && form.formState.errors.body ? (
									<p className="text-xs text-destructive">
										{form.formState.errors.body.message}
									</p>
								) : null}
							</div>
						</FormProvider>

						<DialogFooter>
							<Button
								type="button"
								variant="ghost"
								onClick={() => onOpenChange(false)}
								disabled={isSubmitting}
							>
								Cancel
							</Button>
							<Button
								type="submit"
								className="bg-secondary text-secondary-foreground hover:bg-secondary/90"
								disabled={isSubmitting}
							>
								{isSubmitting ? <LoadingSpinner /> : "Submit"}
							</Button>
						</DialogFooter>
					</form>
				)}
				</div>
			</DialogContent>
		</Dialog>
	);
}
